// @flow
import AddIcon from "@mui/icons-material/Add";
import { Box, Button } from "@mui/material";
import { DataGridPremium, useGridApiRef } from '@mui/x-data-grid-premium'
import { enqueueSnackbar } from "notistack";
import React, { Fragment, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { randomId } from '@mui/x-data-grid-generator';
import { handleAxiosErrors } from "../../../../helpers/errorHandling";
import { muiGridLocales } from "../../../../helpers/muiGridLocales";
import { EDITABLE_MUI_GRID_AS_FIELD } from '../../../../locales/components/namespaces'
import './editableMUIDataGridAsField.css'
import { CustomPagination } from '../dataGridCustom/customPagination'

type Props = {
	formik: Object,
	fieldName: string,
	newRowModel: Object,
	columns: Array<Object>,
	dataGridOtherProps?: Object,
	otherSlots?: Object,
	otherSlotProps?: Object,
	onRowUpdate?: Function,
	rowIdentifier?: string,
	initialState?: Object
};

/* !! Initial value in formik must be an array. !!
 *
 */
const EditableMUIDataGridAsField = (props: Props) => {
	const {
		formik,
		fieldName, // Field name in formik values.
		newRowModel, // Object with properties of new row
		columns,
		dataGridOtherProps, // Pass other data grid properties
		otherSlots = {}, // Pass other custom slot props
		otherSlotProps = {},
		onRowUpdate, // Custom logic on row update
		rowIdentifier = 'id', // Use if row unique property name is not id.
		initialState = {},
		allowAddingNewRow = true,
		disabled = false,
		validateFormBeforeAddingRow = false,
		onAddRow = () => {
		} // Run custom logic on adding row
	} = props
	const apiRef = useGridApiRef()
	const { t } = useTranslation(EDITABLE_MUI_GRID_AS_FIELD)

	const updateFormik = async (rowId) => (
		await formik.setFieldValue(fieldName, [...formik.values[fieldName], { ...newRowModel, [rowIdentifier]: rowId }])
	)

	// On add row, add new object in formik property
	const handleAddRow = (evt, validateBeforeAdding = null) => {
		const validate = validateBeforeAdding !== null ? validateBeforeAdding : validateFormBeforeAddingRow
		const createNewRow = () => {
			// New row must have a unique identifier
			const newRowId = randomId()
			// Update formik state with new row
			updateFormik(newRowId)
			.then(() => {
				// Set new row on edit mode
				apiRef.current.startRowEditMode({ id: newRowId })
				onAddRow(apiRef, newRowId)
			})
		}

		if (validate) {
			formik.validateForm().then(errors => {
				if (Object.keys(errors).length === 0) {
					createNewRow()
				} else {
					Object.keys(errors).forEach(field => {
						formik.setFieldTouched(field, true, false);
					});

				}
			});
		} else {
			createNewRow()
		}
	}

	const processRowUpdate = (updatedRow, originalRow) => {
		const updatedRows = formik.values[fieldName].map((row) => {
			if (row[rowIdentifier] === updatedRow[rowIdentifier]) {
				return { ...row, ...updatedRow }
			}
			return row
		})
		formik.setFieldValue(fieldName, updatedRows)
		if (onRowUpdate) onRowUpdate(updatedRow, originalRow, fieldName, apiRef) // Extra custom logic for row update
		return updatedRow
	}

	const handleProcessRowUpdateError = useCallback((error) => {
		enqueueSnackbar(handleAxiosErrors(error.message, t('An error occurred!')), {
			variant: 'warning',
			autoHideDuration: 3000,
		})
	}, [t])

	// Automatically open new row, when user is on the last cell, of the last grid row, on edit mode, and presses key tab.
	const handleKeyDown = (params, event, details) => {
		const { row, field } = params
		const { api } = details
		const columns = api.getAllColumns()
		const editableColumns = columns.filter(f => f.editable)
		const firstEditableField = editableColumns[0].field
		const lastEditableField = editableColumns[editableColumns.length - 1].field
		const lastRowId = formik.values[fieldName][formik.values[fieldName].length - 1][rowIdentifier]
		const currentRowIndex = formik.values[fieldName].findIndex(r => r[rowIdentifier] === row[rowIdentifier])
		const previousRowId = currentRowIndex > 0 ? formik.values[fieldName][currentRowIndex - 1][rowIdentifier] : null
		const nextRowId = currentRowIndex !== formik.values[fieldName].length - 1 ? formik.values[fieldName][currentRowIndex + 1][rowIdentifier] : null

		if (!event.shiftKey && event.key === 'Tab' && field === lastEditableField) {
			if (lastRowId && lastRowId === row[rowIdentifier]) {
				handleAddRow()
			} else if (nextRowId) {
				const nextRowMode = api.getRowMode(nextRowId)
				if (nextRowMode === 'view') apiRef.current.startRowEditMode({ id: nextRowId })
			}
			const rowMode = api.getRowMode(row.id)
			if(rowMode !== 'view') apiRef.current.stopRowEditMode({ id: row.id })
		}

		if (event.shiftKey && event.key === 'Tab' && field === firstEditableField && currentRowIndex > 0) {
			const previousRowMode = api.getRowMode(previousRowId)
			if (previousRowMode === 'view') apiRef.current.startRowEditMode({ id: previousRowId })
			const rowMode = api.getRowMode(row.id)
			if(rowMode !== 'view') apiRef.current.stopRowEditMode({ id: row.id })
		}
	}

	const handleCellClick = (params, event) => {
		const { id } = params
		if (params.cellMode !== 'edit' && params.isEditable) apiRef.current.startRowEditMode({ id })
	}

	return (
		<Fragment>
			<Box pt={0.5} pb={0.5}>
				{allowAddingNewRow && (
					<Button
						disableElevation
						variant="contained"
						size="small"
						onClick={handleAddRow}
						startIcon={<AddIcon/>}
					>
						{t('Add')}
					</Button>
				)}
			</Box>
			<DataGridPremium
				apiRef={apiRef}
				editMode="row"
				columns={columns}
				rows={formik.values[fieldName]}
				onCellClick={handleCellClick}
				onCellKeyDown={handleKeyDown}
				processRowUpdate={processRowUpdate}
				onProcessRowUpdateError={handleProcessRowUpdateError}
				slots={{
					pagination: CustomPagination,
					...otherSlots
				}}
				slotProps={{
					toolbar: { handleAddNewRow: handleAddRow, disableAddingNewRow: false },
					...otherSlotProps
				}}
				initialState={{ ...initialState, density: 'compact' }}
				{...dataGridOtherProps}
			/>
		</Fragment>
	)
}

export default EditableMUIDataGridAsField